import { entrySpanSeconds } from "./dates.js";
import type { AppUsage, Client, CsvExportRow, Project, TimeEntry } from "./models.js";

const CSV_HEADER: (keyof CsvExportRow)[] = [
  "time_entry_id",
  "client_name",
  "project_name",
  "entry_note",
  "entry_start_time",
  "entry_end_time",
  "session_span_seconds",
  "counted_duration_seconds",
  "app_name",
  "app_bundle_id",
  "app_matcher_type",
  "app_match_value",
  "app_start_time",
  "app_end_time",
  "app_duration_seconds",
  "app_usage_counted",
];

export function buildCsvExportRows(
  entries: TimeEntry[],
  appUsages: AppUsage[],
  projects: Project[],
  clients: Client[],
): CsvExportRow[] {
  const projectsById = new Map(projects.map((project) => [project.id, project]));
  const clientsById = new Map(clients.map((client) => [client.id, client]));
  const usagesByEntry = new Map<string, AppUsage[]>();

  for (const usage of appUsages) {
    const existing = usagesByEntry.get(usage.timeEntryId);
    if (existing) {
      existing.push(usage);
    } else {
      usagesByEntry.set(usage.timeEntryId, [usage]);
    }
  }

  return [...entries]
    .sort((left, right) => left.startTime.localeCompare(right.startTime))
    .flatMap<CsvExportRow>((entry) => {
      const project = projectsById.get(entry.projectId);
      const client = project ? clientsById.get(project.clientId) : undefined;
      const usageSegments = [...(usagesByEntry.get(entry.id) ?? [])].sort((left, right) =>
        left.startTime.localeCompare(right.startTime),
      );

      if (usageSegments.length === 0) {
        const row: CsvExportRow = {
          time_entry_id: entry.id,
          client_name: client?.name ?? "Unknown Client",
          project_name: project?.name ?? "Unknown Project",
          entry_note: entry.note,
          entry_start_time: entry.startTime,
          entry_end_time: entry.endTime,
          session_span_seconds: entrySpanSeconds(entry),
          counted_duration_seconds: entry.countedDurationSeconds,
          app_name: "",
          app_bundle_id: "",
          app_matcher_type: "",
          app_match_value: "",
          app_start_time: "",
          app_end_time: "",
          app_duration_seconds: "",
          app_usage_counted: "",
        };

        return [row];
      }

      return usageSegments.map((usage) => {
        const row: CsvExportRow = {
          time_entry_id: entry.id,
          client_name: client?.name ?? "Unknown Client",
          project_name: project?.name ?? "Unknown Project",
          entry_note: entry.note,
          entry_start_time: entry.startTime,
          entry_end_time: entry.endTime,
          session_span_seconds: entrySpanSeconds(entry),
          counted_duration_seconds: entry.countedDurationSeconds,
          app_name: usage.appName,
          app_bundle_id: usage.bundleId ?? "",
          app_matcher_type: usage.matcherType,
          app_match_value: usage.matchValue,
          app_start_time: usage.startTime,
          app_end_time: usage.endTime,
          app_duration_seconds: entrySpanSeconds({
            startTime: usage.startTime,
            endTime: usage.endTime,
          }),
          app_usage_counted: usage.countsTowardTrackedTime ? "true" : "false",
        };

        return row;
      });
    });
}

export function renderCsv(rows: CsvExportRow[]): string {
  const lines = [CSV_HEADER.join(",")];

  for (const row of rows) {
    lines.push(CSV_HEADER.map((field) => escapeCsv(String(row[field]))).join(","));
  }

  return lines.join("\n");
}

/**
 * Spreadsheet applications (Excel, Numbers, Sheets) interpret a cell as a
 * formula when its value starts with one of a small set of characters.
 * Client, project, and note text is user-controlled, so a malicious or
 * accidental value such as `=cmd|...` could execute when the CSV is opened.
 *
 * This neutralizes such values by prefixing a single quote, which spreadsheet
 * apps treat as a literal-text marker, before the value is quoted for CSV.
 */
export function neutralizeFormula(value: string): string {
  const first = value.charCodeAt(0);
  // "=" (61), "+" (43), "-" (45), "@" (64), tab (9), carriage return (13)
  if (
    first === 61 ||
    first === 43 ||
    first === 45 ||
    first === 64 ||
    first === 9 ||
    first === 13
  ) {
    return `'${value}`;
  }
  return value;
}

function escapeCsv(value: string): string {
  return `"${neutralizeFormula(value).replaceAll("\"", "\"\"")}"`;
}
